import {
  COMPARE_WINDOWS_BY_WINDOW,
  CUSTOM_WINDOW,
  conditionOptionFor,
  normalizeConditionJoin,
  normalizeCompareWindow,
  prettifyAlertValue,
} from '../models/alert.constants'
import type {
  AlertContentDetailsDto,
  AlertSubscriptionDto,
  AlertWriteContentDetailsDto,
  CreateAlertBody,
  UpdateAlertBody,
  ThresholdDto,
} from '../models/alert.dto'
import type {
  AlertDraft,
  AlertLevelPresentation,
  AlertMetricCondition,
  CompareWindow,
} from '../models/alert.types'
import type { AlertChannelType } from '../models/channel.types'
import type { AlertDimension, TriggeredAlertOccurrenceDto } from '../models/history.types'
import type { AlertLevelMetadata, AlertMetricCatalog } from '../models/reports-bridge'

import { alertScalarList } from './alertScalarList'

export interface AlertDimensionMapperDependencies {
  formatDate: (value: string) => string
  metricLabel: AlertMetricCatalog['metricLabel']
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function thresholdAmount(threshold: ThresholdDto): number | null {
  const value = threshold.value
  if (value && typeof value === 'object') {
    const percentile = finiteNumber(value.percentile)
    if (percentile !== null) return percentile
  }
  return finiteNumber(value) ?? finiteNumber(threshold.threshold)
}

function isPercentageThreshold(threshold: ThresholdDto): boolean {
  return Boolean(
    threshold.value &&
    typeof threshold.value === 'object' &&
    Object.prototype.hasOwnProperty.call(threshold.value, 'percentile'),
  )
}

function compareWindowFor(window: AlertDraft['window'], value: unknown): CompareWindow | null {
  if (!window) return null
  const normalized = normalizeCompareWindow(value, window)
  const allowed = COMPARE_WINDOWS_BY_WINDOW[window] as readonly CompareWindow[]
  return normalized && allowed.includes(normalized) ? normalized : (allowed[0] ?? null)
}

function cloneThreshold(threshold: ThresholdDto): ThresholdDto {
  const clone = { ...threshold }
  if (clone.value && typeof clone.value === 'object') clone.value = { ...clone.value }
  return clone
}

function canonicalContentDetails(details: AlertContentDetailsDto): AlertWriteContentDetailsDto {
  const customWindowDays = readCustomWindowDays(details)
  const compareWindow = normalizeCompareWindow(details.compare_window, details.alert_window.type)
  const {
    compare_window: _legacyCompareWindow,
    custom_window_days: _legacyCustomWindowDays,
    ...canonicalDetails
  } = details
  return {
    ...canonicalDetails,
    alert_window: {
      type: details.alert_window.type,
      ...(details.alert_window.type === 'custom' && customWindowDays !== null
        ? { custom_window_days: customWindowDays }
        : {}),
    },
    ...(compareWindow ? { compare_window: compareWindow } : {}),
    dimensions: [...details.dimensions],
    metrics: [...details.metrics],
    filters: details.filters.map((filter) => ({ ...filter })),
    thresholds: details.thresholds.map(cloneThreshold),
  }
}

export function readCustomWindowDays(details: AlertContentDetailsDto): number | null {
  if (details.alert_window.type !== 'custom') return null
  return (
    finiteNumber(details.alert_window.custom_window_days) ??
    finiteNumber(details.custom_window_days)
  )
}

export function alertMetricField(details: AlertContentDetailsDto): string | null {
  return details.metrics[0] || details.thresholds[0]?.field || null
}

function conditionFromThreshold(
  threshold: ThresholdDto,
  details: AlertContentDetailsDto,
  catalog: AlertMetricCatalog,
): AlertMetricCondition | null {
  const option = conditionOptionFor(threshold.match_mode)
  const field = threshold.field
  if (!option || !field) return null

  const mode = isPercentageThreshold(threshold) ? 'percentage' : 'value'
  const rawAmount = thresholdAmount(threshold)
  const column = catalog.getColumn(field) ?? {
    field,
    header: catalog.metricLabel(field),
    numeric: true,
  }

  return {
    column,
    operator: option.operator,
    mode,
    amount: rawAmount === null ? null : mode === 'percentage' ? rawAmount * 100 : rawAmount,
    compareWindow:
      mode === 'percentage'
        ? normalizeCompareWindow(details.compare_window, details.alert_window.type)
        : null,
  }
}

/** Port of getConditionInfo: DTO vocabulary in, typed domain condition out. Only the FIRST
 * threshold — the list's single headline condition badge, not the wizard's full editable set. */
export function alertCondition(
  details: AlertContentDetailsDto,
  catalog: AlertMetricCatalog,
): AlertMetricCondition | null {
  const threshold = details.thresholds[0]
  return threshold ? conditionFromThreshold(threshold, details, catalog) : null
}

/** Every threshold on the subscription — the wizard's editable multi-metric state. A subscription
 * holds all of an alert's metrics in one `content_details` blob (confirmed against the backend:
 * `content_details` is replaced wholesale, so N concurrent single-metric writes to the same id
 * raced and silently dropped each other's data — see `editableContentDetails`). */
export function alertConditions(
  details: AlertContentDetailsDto,
  catalog: AlertMetricCatalog,
): AlertMetricCondition[] {
  return details.thresholds.flatMap((threshold) => {
    const condition = conditionFromThreshold(threshold, details, catalog)
    return condition ? [condition] : []
  })
}

export function alertConditionForMetric(
  details: AlertContentDetailsDto,
  catalog: AlertMetricCatalog,
  metricField: string | null,
): AlertMetricCondition | null {
  const threshold =
    details.thresholds.find((item) => item.field === metricField) ?? details.thresholds[0]
  return threshold ? conditionFromThreshold(threshold, details, catalog) : null
}

export function alertConditionJoin(details: AlertContentDetailsDto) {
  return normalizeConditionJoin(details.condition_operator)
}

function mappedColumn(field: string, catalog: AlertMetricCatalog) {
  return (
    catalog.getColumn(field) ?? {
      field,
      header: catalog.metricLabel(field),
      numeric: true,
    }
  )
}

export function alertSubscriptionChannels(
  value: AlertSubscriptionDto['channel_type'],
): AlertChannelType[] {
  const values = alertScalarList(value)
  return values.filter(
    (channel): channel is AlertChannelType =>
      channel === 'mail' || channel === 'whatsapp' || channel === 'slack' || channel === 'teams',
  )
}

export interface AlertDraftMapperOptions {
  readOnly?: boolean
  workspaceIds?: readonly (number | string)[]
}

/** Hydrate edit state without unsafe parsing of legacy stringified collections. */
export function alertDraftFromDto(
  subscription: AlertSubscriptionDto,
  catalog: AlertMetricCatalog,
  levels: AlertLevelMetadata,
  options: AlertDraftMapperOptions = {},
): AlertDraft {
  const raw = canonicalContentDetails(subscription.content_details)
  const partition = raw.selected_table_name || raw.model
  const conditions = alertConditions(raw, catalog)
  return {
    id: subscription.id,
    name: raw.name,
    level: levels.levelByPartition(partition, options.workspaceIds),
    groupBy: raw.dimensions.map((field) => mappedColumn(field, catalog)),
    filters: raw.filters.map((filter) => ({ ...filter })),
    window: raw.alert_window.type,
    customWindowDays: readCustomWindowDays(raw),
    metrics: conditions.map((condition) => ({
      ...condition,
      compareWindow:
        condition.mode === 'percentage'
          ? compareWindowFor(raw.alert_window.type, raw.compare_window)
          : null,
    })),
    conditionJoin: normalizeConditionJoin(raw.condition_operator),
    channels: alertSubscriptionChannels(subscription.channel_type),
    recipientEmails: alertScalarList(subscription.allowed_emails),
    whatsappNumbers: alertScalarList(subscription.whatsapp_numbers),
    readOnly: options.readOnly ?? false,
    raw,
  }
}

/** One body for EVERY metric (ORP-XXXXX): `content_details` is a subscription's whole state,
 * replaced wholesale on write — N separate single-metric requests to the same id raced and
 * silently dropped each other's data. `compare_window` stays one shared field per alert, not
 * per metric — the first percentage-mode condition's value wins, same as it always implicitly
 * did when only one metric's request could ever "win" the race. */
function editableContentDetails(
  draft: AlertDraft,
  conditions: readonly AlertMetricCondition[],
): AlertWriteContentDetailsDto {
  if (!draft.level || !draft.window || !conditions.length) {
    throw new Error('Validated alert structure is incomplete')
  }
  if (
    draft.window === 'custom' &&
    (draft.customWindowDays === null ||
      !Number.isInteger(draft.customWindowDays) ||
      draft.customWindowDays < CUSTOM_WINDOW.min ||
      draft.customWindowDays > CUSTOM_WINDOW.max)
  ) {
    throw new Error('Validated custom window is outside the supported range')
  }
  const percentageCondition = conditions.find((condition) => condition.mode === 'percentage')
  const compareWindow = percentageCondition
    ? compareWindowFor(draft.window, percentageCondition.compareWindow)
    : null
  if (percentageCondition && !compareWindow) {
    throw new Error('Validated compare window is incomplete')
  }

  return {
    name: draft.name.trim(),
    model: draft.level.partition_name,
    report_id: draft.level.report_id,
    selected_table_name: draft.level.partition_name,
    dimensions: draft.groupBy.map((column) => column.field),
    metrics: conditions.map((condition) => condition.column.field),
    alert_window: {
      type: draft.window,
      ...(draft.window === 'custom' && draft.customWindowDays !== null
        ? { custom_window_days: draft.customWindowDays }
        : {}),
    },
    filters: draft.filters.map((filter) => ({ ...filter })),
    condition_operator: draft.conditionJoin,
    ...(compareWindow ? { compare_window: compareWindow } : {}),
    thresholds: conditions.map((condition) => {
      const amount = condition.amount
      if (amount === null) throw new Error('Validated alert structure is incomplete')
      const percentage = condition.mode === 'percentage'
      return {
        field: condition.column.field,
        match_mode: condition.operator,
        value: percentage ? { percentile: amount / 100 } : amount,
        is_percentage: percentage,
      }
    }),
  }
}

function notificationBody(
  draft: AlertDraft,
  contentDetails: AlertWriteContentDetailsDto,
): CreateAlertBody {
  return {
    channels: draft.channels.map((channel_type) => ({ channel_type, frequency: 'daily' })),
    content_details: contentDetails,
    whatsapp_numbers: [...draft.whatsappNumbers],
    allowed_emails: [...draft.recipientEmails],
  }
}

/** Maps the WHOLE draft — every metric together — to one subscription body. */
export function alertDraftToDto(draft: AlertDraft): CreateAlertBody {
  if (draft.readOnly) {
    if (!draft.raw) throw new Error('Read-only alert is missing its original structure')
    return notificationBody(draft, canonicalContentDetails(draft.raw))
  }
  if (!draft.metrics.length) throw new Error('Editable alert is missing its metric conditions')
  return notificationBody(draft, editableContentDetails(draft, draft.metrics))
}

export function alertDraftToUpdateDto(draft: AlertDraft): UpdateAlertBody {
  return { ...alertDraftToDto(draft), frequency: 'daily' }
}

export function alertFilterSubtext(
  details: AlertContentDetailsDto,
): AlertContentDetailsDto['filters'][number] | string | null {
  return details.filters[0] ?? (details.description || null)
}

export function alertLevelPresentation(
  details: AlertContentDetailsDto,
  levels: AlertLevelMetadata,
  workspaceIds?: readonly (number | string)[],
): AlertLevelPresentation {
  const partition = details.selected_table_name || details.model
  if (!partition) return { source: 'default', labelKey: 'alerts.level.defaultCampaign' }
  const level = levels.levelByPartition(partition, workspaceIds)
  if (level) return { source: 'catalog', label: level.name }
  return {
    source: 'partition',
    labelKey: 'alerts.level.unknown',
    labelParams: { value: prettifyAlertValue(partition) },
  }
}

function dimensionValue(
  value: unknown,
  formatDate: AlertDimensionMapperDependencies['formatDate'],
): string | null {
  if (typeof value !== 'string' || !value || value.trim() === '__all__') return null
  if (/^\d{4}-\d{2}-\d{2}[tT ]/u.test(value)) return formatDate(value)
  return prettifyAlertValue(value)
}

/** Port of getDimensionInfo; returns the domain's empty array instead of a nullable list. */
export function alertDimensions(
  occurrence: TriggeredAlertOccurrenceDto,
  dependencies: AlertDimensionMapperDependencies,
): AlertDimension[] {
  const result: AlertDimension[] = []
  const add = (field: string, rawValue: unknown): void => {
    const value = dimensionValue(rawValue, dependencies.formatDate)
    if (value) result.push({ label: dependencies.metricLabel(field), value })
  }

  const dimension = occurrence.details?.dimension ?? occurrence.dimension
  if (dimension && typeof dimension === 'object') {
    for (const [field, value] of Object.entries(dimension)) add(field, value)
  } else if (typeof dimension === 'string') {
    add('dimension', dimension)
  }
  if (result.length) return result

  const segmentName = occurrence.segment_name
  const subSegmentName = occurrence.details?.sub_segment_name ?? occurrence.sub_segment_name
  if (segmentName && subSegmentName) {
    const [field = '', segment = ''] = segmentName.split(' - ')
    if (segment === subSegmentName) {
      add(field || 'segment', subSegmentName)
      if (result.length) return result
    }
  }
  if (subSegmentName) add('segment', subSegmentName)
  return result
}

/** Convenience read-side projection kept pure for the later loadAll ticket. */
export function alertReadVocabulary(
  subscription: AlertSubscriptionDto,
  catalog: AlertMetricCatalog,
  levels: AlertLevelMetadata,
  workspaceIds?: readonly (number | string)[],
): {
  condition: AlertMetricCondition | null
  filterSubtext: AlertContentDetailsDto['filters'][number] | string | null
  level: AlertLevelPresentation
  metricField: string | null
  compareWindow: CompareWindow | null
} {
  const details = subscription.content_details
  const condition = alertCondition(details, catalog)
  return {
    condition,
    filterSubtext: alertFilterSubtext(details),
    level: alertLevelPresentation(details, levels, workspaceIds),
    metricField: alertMetricField(details),
    compareWindow: condition?.compareWindow ?? null,
  }
}
